import * as Sentry from "@sentry/react";
import type { ErrorEvent } from "@sentry/react";

/** alert.category tag values — must match Sentry dashboard alert filters. */
export const ALERT_CATEGORY = {
  FRONTEND_CRASH: "frontend_crash",
  LESSON_LOAD_FAILURE: "lesson_load_failure",
  AUTH: "auth",
} as const;

/** @deprecated use ALERT_CATEGORY.FRONTEND_CRASH */
export const FRONTEND_CRASH_ALERT_CATEGORY = ALERT_CATEGORY.FRONTEND_CRASH;

/** True in production builds when a DSN is baked into the bundle. */
export function isSentryEnabled(): boolean {
  return (
    import.meta.env.PROD && Boolean(import.meta.env.VITE_SENTRY_DSN?.trim())
  );
}

export function getSentryEnvironment(): string {
  return import.meta.env.MODE ?? "development";
}

/**
 * Applied to every error event via Sentry.init beforeSend.
 * Sets `alert.category: frontend_crash` as the default, but preserves any
 * category already set via withScope/setTag (e.g. auth, lesson_load_failure).
 */
export function sentryBeforeSend(event: ErrorEvent): ErrorEvent {
  event.tags = {
    "alert.category": ALERT_CATEGORY.FRONTEND_CRASH,
    ...event.tags,
  };
  return event;
}

/** Error boundaries: global (root ErrorBoundary). */
export function captureFrontendCrash(
  error: unknown,
  context?: { boundary?: "route" | "global" },
): void {
  if (!isSentryEnabled()) return;

  Sentry.withScope((scope) => {
    scope.setTag("alert.category", ALERT_CATEGORY.FRONTEND_CRASH);
    if (context?.boundary) {
      scope.setTag("error.boundary", context.boundary);
    }
    Sentry.captureException(error);
  });
}

/**
 * Course Lesson Loading Failures alert (alert.category: lesson_load_failure).
 * Call when Shaka player fails to load a lecture video.
 */
export function captureLessonLoadFailure(
  error: unknown,
  context?: { lectureId?: string; source?: string },
): void {
  if (!isSentryEnabled()) return;

  Sentry.withScope((scope) => {
    scope.setTag("alert.category", ALERT_CATEGORY.LESSON_LOAD_FAILURE);
    if (context?.source) scope.setTag("lesson.source", context.source);
    if (context?.lectureId) scope.setExtra("lectureId", context.lectureId);
    Sentry.captureException(error);
  });
}

/**
 * Auth failures alert (alert.category: auth).
 * Call when token refresh permanently fails or a 401/403 is not recoverable.
 */
export function captureAuthFailure(
  error: unknown,
  context?: { path?: string; status?: number },
): void {
  if (!isSentryEnabled()) return;

  Sentry.withScope((scope) => {
    scope.setTag("alert.category", ALERT_CATEGORY.AUTH);
    if (context?.status)
      scope.setTag("http.status_code", String(context.status));
    if (context?.path) scope.setExtra("path", context.path);
    Sentry.captureException(error);
  });
}
